var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect } from 'react';
import { makeStyles, Checkbox, Dialog, DialogTitle, DialogActions, Container, DialogButton } from "../../../index";
import { useUseCase } from "../../../hooks/useUseCase";
import { useTranslation } from "../../../hooks/useTranslation";
import { useDeclarativeLoading } from "../../../hooks/useLoading";
import { isMultipleSelection } from "../../../models/PmlFileSelect/SelectedPmlFiles";
import { initialPmlFileSelectDialogState } from "./PmlFileSelectDialogState";
import { OpenPmlFileSelectDialogUseCase } from "./usecases/OpenPmlFileSelectDialogUseCase";
import { PmlFileSelectDialogToolbar } from "./components/PmlFileSelectDialogToolbar";
import { UnselectPmlFileUseCase } from "./usecases/UnselectPmlFileUseCase";
import { SelectPmlFileUseCase } from "./usecases/SelectPmlFileUseCase";
import { ChangeFilterUseCase } from "./usecases/ChangeFilterUseCase";
import { ChangePaginationUseCase } from "./usecases/ChangePaginationUseCase";
import { ClosePmlFileSelectDialogUseCase } from "./usecases/ClosePmlFileSelectDialogUseCase";
import { ClearFilterUseCase } from "./usecases/ClearFilterUseCase";
import { PmlFileSelectDialogContent } from "./components/PmlFileSelectDialogContent";
import { ChangeOwnerNameVisibilityUseCase } from "./usecases/ChangeOwnerNameVisibilityUseCase";
var useStyles = makeStyles(function (theme) { return ({
    root: {},
    container: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: 'none',
        padding: 0,
    },
    actions: {
        position: 'relative',
    },
    checkbox: {
        position: 'absolute',
        right: theme.spacing(3),
        top: '50%',
        transform: 'translate(0, -50%)',
    },
}); });
export var PmlFileSelectDialog = function (props) {
    var open = props.open, kind = props.kind, defaultPmlFile = props.defaultPmlFile, selectionMode = props.selectionMode, ownerNameOptionVisible = props.ownerNameOptionVisible, onSelect = props.onSelect, onSelectMultiple = props.onSelectMultiple, onCancel = props.onCancel, others = __rest(props, ["open", "kind", "defaultPmlFile", "selectionMode", "ownerNameOptionVisible", "onSelect", "onSelectMultiple", "onCancel"]);
    var _a = useUseCase(initialPmlFileSelectDialogState), state = _a.state, usecase = _a.usecase;
    var t = useTranslation().t;
    var classes = useStyles();
    // ファイルの読み込み中にローディングを表示する
    useDeclarativeLoading('loading', state.isLoading);
    useEffect(function () {
        open
            ? usecase(new OpenPmlFileSelectDialogUseCase()).execute({ defaultPmlFile: defaultPmlFile, selectionMode: selectionMode, kind: kind })
            : usecase(new ClosePmlFileSelectDialogUseCase()).execute();
    }, [open, defaultPmlFile, usecase, selectionMode]);
    var handleCancel = useCallback(function () {
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }, [onCancel]);
    var handleSelect = useCallback(function () {
        if (state.selectedPmlFiles.pmlFiles.length === 0)
            return;
        if (isMultipleSelection(selectionMode)) {
            onSelectMultiple === null || onSelectMultiple === void 0 ? void 0 : onSelectMultiple(state.selectedPmlFiles.pmlFiles, state.showOwnerName);
        }
        else {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(state.selectedPmlFiles.pmlFiles[0]);
        }
    }, [onSelect, onSelectMultiple, selectionMode, state.selectedPmlFiles.pmlFiles, state.showOwnerName]);
    var handleSelectPmlFile = useCallback(function (PmlFile, selected) {
        selected
            ? usecase(new UnselectPmlFileUseCase()).execute(PmlFile)
            : usecase(new SelectPmlFileUseCase()).execute(PmlFile);
    }, [usecase]);
    var handleFilterChange = useCallback(function (filterCondition) {
        usecase(new ChangeFilterUseCase()).execute({ filterCondition: filterCondition });
    }, [usecase]);
    var handleFilterClear = useCallback(function () {
        usecase(new ClearFilterUseCase()).execute({ filterCondition: state.filterCondition });
    }, [state.filterCondition, usecase]);
    var handleChangePagination = useCallback(function (nextPage) {
        usecase(new ChangePaginationUseCase()).execute({
            nextPage: nextPage,
            filterCondition: state.filterCondition,
            sortCondition: state.sortCondition,
        });
    }, [state.filterCondition, state.sortCondition, usecase]);
    var handleChangeOwnerNameVisibility = useCallback(function (showOwnerName) {
        usecase(new ChangeOwnerNameVisibilityUseCase()).execute(showOwnerName);
    }, [usecase]);
    return (React.createElement(Dialog, __assign({ className: classes.root, open: open, fullScreen: true }, others),
        React.createElement(DialogTitle, null, t('ファイルを選ぶ')),
        React.createElement(Container, { className: classes.container },
            React.createElement(PmlFileSelectDialogToolbar, { kind: kind, onFilterChange: handleFilterChange, onFilterClear: handleFilterClear, filterCondition: state.filterCondition, isFilterEmpty: state.filterCondition.isEmpty, pagination: state.pagination, onChangePagination: handleChangePagination }),
            React.createElement(PmlFileSelectDialogContent, { pmlFiles: state.pmlFiles, onSelectPmlFile: handleSelectPmlFile, selectedPmlFileId: state.selectedPmlFileId, sortCondition: state.sortCondition, isMultipleSelection: state.selectedPmlFiles.isMultipleSelection, showOwnerName: state.showOwnerName, onChangePagination: handleChangePagination, isLoading: state.isLoading })),
        React.createElement(DialogActions, { className: classes.actions },
            React.createElement(DialogButton, { onClick: handleCancel }, t('キャンセル')),
            React.createElement(DialogButton, { fill: true, onClick: handleSelect, disabled: state.selectedPmlFileId.length === 0 }, t('選ぶ')),
            ownerNameOptionVisible && (React.createElement("div", { className: classes.checkbox },
                React.createElement(Checkbox, { label: t('所有者名を表示する'), value: state.showOwnerName, onChange: handleChangeOwnerNameVisibility }))))));
};
